import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { anchoredMenuPosition } from './anchoredMenuPosition';

interface AnchoredMenuProps {
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly className?: string;
  readonly children: ReactNode;
  readonly label: string;
  readonly onDismiss: () => void;
  readonly open: boolean;
}

function enabledMenuItems(menu: HTMLElement | null): HTMLElement[] {
  return [
    ...(menu?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]') ?? []),
  ].filter((item) => !item.matches(':disabled, [aria-disabled="true"]'));
}

export function AnchoredMenu({
  anchorRef,
  children,
  className,
  label,
  onDismiss,
  open,
}: AnchoredMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const restoreFocusRef = useRef(true);

  useLayoutEffect(() => {
    if (!open) return;
    restoreFocusRef.current = true;
    const focusReturnTarget = anchorRef.current;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (!anchor || !menu) return;
      const position = anchoredMenuPosition(
        anchor.getBoundingClientRect(),
        menu.getBoundingClientRect(),
        { height: window.innerHeight, width: window.innerWidth },
      );
      menu.style.left = `${position.left}px`;
      menu.style.top = `${position.top}px`;
      menu.style.visibility = 'visible';
      menu.dataset.placement = position.placement;
    };
    const queuePositionUpdate = () => {
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;
        updatePosition();
      });
    };

    updatePosition();
    enabledMenuItems(menuRef.current)[0]?.focus();
    window.addEventListener('resize', queuePositionUpdate);
    window.addEventListener('scroll', queuePositionUpdate, true);
    return () => {
      window.removeEventListener('resize', queuePositionUpdate);
      window.removeEventListener('scroll', queuePositionUpdate, true);
      if (animationFrameRef.current !== null)
        window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
      if (restoreFocusRef.current && focusReturnTarget?.isConnected) focusReturnTarget.focus();
    };
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        restoreFocusRef.current = false;
        onDismiss();
      }
    };
    const dismissOnFocusOutside = (event: FocusEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        restoreFocusRef.current = false;
        onDismiss();
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('focusin', dismissOnFocusOutside);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('focusin', dismissOnFocusOutside);
    };
  }, [anchorRef, onDismiss, open]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = enabledMenuItems(menuRef.current);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || items.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowUp'
            ? (currentIndex - 1 + items.length) % items.length
            : (currentIndex + 1) % items.length;
    items[nextIndex]?.focus();
  };

  if (!open) return null;
  return createPortal(
    <div
      aria-label={label}
      className={`menu-popover layer-menu-popover anchored-menu-popover${className ? ` ${className}` : ''}`}
      onKeyDown={handleKeyDown}
      ref={menuRef}
      role="menu"
      style={{ left: 0, top: 0, visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
}
